import React, { useState,useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link,useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI } from '../utils/api';
import { statusColor, statusLabel, formatDate, formatBudget, isClosed } from '../utils/bookings';
import { 
  CalendarIcon, 
  CurrencyDollarIcon, 
  StarIcon, 
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const DashboardSmart = () => {
  const { user, loading, isSmart } = useAuth();
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('missions');
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/login');
    } else if (!isSmart) {
      navigate('/dashboard');
    }
  }, [user, loading, isSmart, navigate]);

  // Réservations affectées à ce Smart, dans la collection `reservations`
  useEffect(() => {
    if (!user || !isSmart) return;
    bookingsAPI.getAll()
      .then((res) => setBookings(res.data || []))
      .catch(() => setBookings([]));
  }, [user, isSmart]);

  // Les chiffres proviennent de la fiche `smarts` et des réservations réelles
  const fiche = user?.smart;
  const paid = bookings.filter((booking) => booking.montantPaye);
  const revenue = paid.reduce((sum, booking) => sum + (booking.montantPaye || 0), 0);

  const stats = [
    { label: 'Missions', value: String(fiche?.missionCount ?? bookings.length), icon: CalendarIcon, color: '#F97316' },
    { label: 'Revenus encaissés', value: revenue.toLocaleString('fr-FR'), unit: 'FCFA', icon: CurrencyDollarIcon, color: '#10B981' },
    { label: 'Note moyenne', value: String(fiche?.rating ?? 0), icon: StarIcon, suffix: '⭐', color: '#F59E0B' },
    { label: 'Avis reçus', value: String(fiche?.reviewCount ?? 0), icon: ChartBarIcon, color: '#3B82F6' },
  ];

  const upcomingMissions = bookings.filter((booking) => !isClosed(booking.statut));
  const recentEarnings = paid;

  const getStatusBadge = (statut) => (
    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusColor(statut)}`}>
      {statusLabel(statut)}
    </span>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#F97316] to-[#e8630a] pt-12 pb-24 mb-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl lg:text-4xl font-display font-bold text-white">
                    Tableau de bord Smart
                  </h1>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm text-white">
                    ⭐ Smart Pro
                  </span>
                </div>
                <p className="text-white/80 mt-1">Bonjour, {user?.nom || 'Smart'} ! Voici votre activité</p>
              </div>
              <div className="flex gap-3">
                <Link to="/profile" className="bg-white/20 backdrop-blur-sm text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-white/30 transition-all flex items-center gap-2">
                  <Cog6ToothIcon className="h-5 w-5" />
                  Profil
                </Link>
                <Link to="/availability" className="bg-white text-[#F97316] px-5 py-2.5 rounded-xl font-semibold hover:shadow-xl transition-all">
                  Mes disponibilités
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container-custom -mt-16 pb-12">
        {/* Statistiques */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#111111] rounded-2xl p-5 border border-[#252525] hover:border-[#F97316]/30 transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center group-hover:bg-[#F97316]/20 transition">
                  <stat.icon className="h-5 w-5" style={{ color: stat.color }} />
                </div>
                <span className="text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">{stat.change}</span>
              </div>
              <p className="text-3xl font-bold text-white">
                {stat.value}
                {stat.suffix && ` ${stat.suffix}`}
                {stat.unit && <span className="text-sm text-[#AAAAAA] ml-1">{stat.unit}</span>}
              </p>
              <p className="text-sm text-[#AAAAAA] mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#252525] pb-2">
          {['missions', 'earnings', 'reviews'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
                activeTab === tab
                  ? 'bg-[#F97316] text-white'
                  : 'text-[#AAAAAA] hover:text-white'
              }`}
            >
              {tab === 'missions' && '📋 Missions'}
              {tab === 'earnings' && '💰 Revenus'}
              {tab === 'reviews' && '⭐ Avis'}
            </button>
          ))}
        </div>

        {/* Missions à venir */}
        {activeTab === 'missions' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Missions à venir</h2>
              <button className="text-sm text-[#F97316] hover:underline">Voir tout →</button>
            </div>
            
            {upcomingMissions.length === 0 && (
              <p className="text-[#666666] text-center py-10">Aucune mission en cours.</p>
            )}

            {upcomingMissions.map((mission, index) => (
              <motion.div
                key={mission._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#111111] rounded-2xl border border-[#252525] p-5 hover:border-[#F97316]/30 transition-all"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-white">{mission.description || 'Demande'}</h3>
                      {getStatusBadge(mission.statut)}
                    </div>
                    <p className="text-[#AAAAAA] mb-2">Client : {mission.clientName || '—'}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-[#AAAAAA]">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(mission.dateIntervention, true)}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {mission.urgence || 'Normal'}
                      </span>
                      <span className="flex items-center gap-1">
                        📍 {mission.adresse || '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        💰 {formatBudget(mission.budget)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 rounded-xl bg-[#1a1a1a] text-white text-sm hover:bg-[#252525] transition">
                      Détails
                    </button>
                    <button className="px-4 py-2 rounded-xl bg-[#F97316]/10 text-[#F97316] text-sm hover:bg-[#F97316]/20 transition">
                      Contacter
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Calendrier simplifié */}
            <div className="bg-[#111111] rounded-2xl border border-[#252525] p-5 mt-6">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-[#F97316]" />
                Mon calendrier
              </h3>
              <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                <p className="text-[#AAAAAA] text-sm">
                  📅 Vous avez 3 missions cette semaine
                </p>
                <button className="mt-3 text-sm text-[#F97316] hover:underline">
                  Gérer mon agenda →
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Revenus */}
        {activeTab === 'earnings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Résumé revenus */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-[#F97316]/20 to-[#F97316]/5 rounded-2xl p-5 border border-[#F97316]/20">
                <p className="text-[#AAAAAA] text-sm">Revenus du mois</p>
                <p className="text-3xl font-bold text-white">450 000 FCFA</p>
                <p className="text-green-500 text-sm mt-1">↑ +8% vs mois dernier</p>
              </div>
              <div className="bg-[#111111] rounded-2xl p-5 border border-[#252525]">
                <p className="text-[#AAAAAA] text-sm">Revenus totaux</p>
                <p className="text-2xl font-bold text-white">1 250 000 FCFA</p>
              </div>
              <div className="bg-[#111111] rounded-2xl p-5 border border-[#252525]">
                <p className="text-[#AAAAAA] text-sm">En attente de paiement</p>
                <p className="text-2xl font-bold text-yellow-500">35 000 FCFA</p>
              </div>
            </div>

            {/* Historique des revenus */}
            <div className="bg-[#111111] rounded-2xl border border-[#252525] overflow-hidden">
              <div className="p-5 border-b border-[#252525]">
                <h3 className="font-semibold text-white">Historique des paiements</h3>
              </div>
              <div className="divide-y divide-[#252525]">
                {recentEarnings.length === 0 && (
                  <p className="p-6 text-center text-[#666666]">Aucun paiement encaissé.</p>
                )}

                {recentEarnings.map((earning) => (
                  <div key={earning._id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{earning.description || 'Prestation'}</p>
                      <p className="text-sm text-[#AAAAAA]">
                        {earning.clientName || '—'} · {formatDate(earning.paidAt || earning.dateIntervention)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#F97316]">{formatBudget(earning.montantPaye)}</p>
                      <span className="text-xs text-green-500">{statusLabel(earning.statut)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Avis */}
        {activeTab === 'reviews' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Score global */}
              <div className="bg-[#111111] rounded-2xl border border-[#252525] p-6 text-center">
                <div className="text-5xl font-bold text-[#F97316] mb-2">{fiche?.rating ?? 0}</div>
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-[#AAAAAA]">Basé sur {fiche?.reviewCount ?? 0} avis</p>
                <div className="mt-4 pt-4 border-t border-[#252525]">
                  <div className="flex justify-between text-sm mb-1">
                    <span>5★</span>
                    <span className="text-[#AAAAAA]">85%</span>
                  </div>
                  <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                    <div className="h-full w-[85%] bg-yellow-500 rounded-full"></div>
                  </div>
                  <div className="flex justify-between text-sm mt-2 mb-1">
                    <span>4★</span>
                    <span className="text-[#AAAAAA]">12%</span>
                  </div>
                  <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                    <div className="h-full w-[12%] bg-yellow-500 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Derniers avis */}
              <div className="lg:col-span-2 bg-[#111111] rounded-2xl border border-[#252525] p-5">
                <h3 className="font-semibold text-white mb-4">Derniers avis clients</h3>
                <div className="space-y-4">
                  {[
                    { client: 'Marie T.', rating: 5, comment: 'Excellent travail, très professionnel !', date: '2024-05-08' },
                    { client: 'Jean K.', rating: 5, comment: 'Intervention rapide et efficace', date: '2024-05-06' },
                    { client: 'Ama D.', rating: 4, comment: 'Bon service, ponctuel', date: '2024-05-03' },
                  ].map((review, idx) => (
                    <div key={idx} className="pb-4 border-b border-[#252525] last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">{review.client}</span>
                        <span className="text-xs text-[#666666]">{review.date}</span>
                      </div>
                      <div className="flex gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon key={i} className={`h-4 w-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-[#333333]'}`} />
                        ))}
                      </div>
                      <p className="text-[#AAAAAA] text-sm">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Section aide */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 p-5 bg-gradient-to-r from-[#111111] to-[#0a0a0a] rounded-2xl border border-[#252525] flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#F97316]" />
            </div>
            <div>
              <p className="text-white font-semibold">Support Smart</p>
              <p className="text-sm text-[#AAAAAA]">Une question ? Notre équipe est là pour vous aider</p>
            </div>
          </div>
          <Link to="/contact" className="text-[#F97316] font-semibold hover:underline">
            Contacter le support →
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardSmart;